import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, BehaviorSubject, from, of, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, take, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';
import { AuthRefreshService } from '../services/auth-refresh.service';
import { AuthTokenStoreService } from '../services/auth-token-store.service';
import { SecureStorageService } from '../services/secure-storage.service';
import { AdminAuthService } from '../../modules/admin/services/admin-auth.service';

@Injectable()
export class SecurityHeadersInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  private readonly refreshFailedToken = '__REFRESH_FAILED__';

  constructor(
    private readonly router: Router,
    private readonly authRefreshService: AuthRefreshService,
    private readonly authTokenStore: AuthTokenStoreService,
    private readonly secureStorage: SecureStorageService,
    private readonly adminAuthService: AdminAuthService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isExternalRequest = /^https?:\/\//i.test(req.url) && !req.url.includes(environment.apiUrl);

    if (isExternalRequest) {
      return next.handle(req);
    }

    const isAdminContext = this.isAdminContext();

    return from(this.getAccessToken(isAdminContext)).pipe(
      switchMap((accessToken) => next.handle(this.attachSecurityContext(req, accessToken))),
      catchError((error: HttpErrorResponse) => {
        if (!this.shouldHandleUnauthorized(error, req.url)) {
          return throwError(() => error);
        }

        if (!this.isRefreshing) {
          this.isRefreshing = true;
          this.refreshTokenSubject.next(null);

          return this.refreshTokenForContext(isAdminContext).pipe(
            tap(() => {
              this.refreshTokenSubject.next('refreshed');
            }),
            switchMap(() => from(this.getAccessToken(isAdminContext))),
            switchMap((accessToken) => next.handle(this.attachSecurityContext(req, accessToken, true))),
            catchError((refreshError: HttpErrorResponse) => {
              const failedRefreshUrl = `${refreshError?.url || ''}`;
              this.refreshTokenSubject.next(this.refreshFailedToken);
              if (failedRefreshUrl.includes('/auth/refresh-token')) {
                return this.clearSessionAfterRefreshFailure(isAdminContext, refreshError);
              }
              return throwError(() => refreshError);
            }),
            finalize(() => {
              this.isRefreshing = false;
            })
          );
        }

        return this.refreshTokenSubject.pipe(
          filter(token => token !== null),
          take(1),
          switchMap((token) => token === this.refreshFailedToken
            ? throwError(() => error)
            : from(this.getAccessToken(isAdminContext)).pipe(
              switchMap((accessToken) => next.handle(this.attachSecurityContext(req, accessToken, true)))
            ))
        );
      })
    );
  }

  private attachSecurityContext(
    req: HttpRequest<any>,
    accessToken: string | null,
    replaceAuthorization = false
  ): HttpRequest<any> {
    const headers = replaceAuthorization
      ? (accessToken
        ? req.headers.set('Authorization', `Bearer ${accessToken}`)
        : req.headers.delete('Authorization'))
      : (req.headers.has('Authorization') || !accessToken
        ? req.headers
        : req.headers.set('Authorization', `Bearer ${accessToken}`));

    const impersonatingUserID = this.impersonatingUserID();
    const requestHeaders = impersonatingUserID
      ? headers.set('X-Impersonating-User-ID', impersonatingUserID)
      : headers;

    return req.clone({
      withCredentials: true,
      headers: requestHeaders,
    });
  }

  private getAccessToken(isAdminContext: boolean): Promise<string | null> {
    return isAdminContext
      ? this.secureStorage.getItem('admin_access_token')
      : Promise.resolve(this.authTokenStore.getAccessToken());
  }

  private refreshTokenForContext(isAdminContext: boolean): Observable<any> {
    return isAdminContext
      ? this.adminAuthService.refreshToken()
      : this.authRefreshService.refreshToken();
  }

  private clearSessionAfterRefreshFailure(
    isAdminContext: boolean,
    refreshError: HttpErrorResponse
  ): Observable<never> {
    const clearSession$ = isAdminContext
      ? from(this.adminAuthService.clearLocalSession())
      : of(this.clearCustomerSession());

    return clearSession$.pipe(
      catchError(() => of(undefined)),
      tap(() => {
        void this.router.navigate([isAdminContext ? '/admin/login' : '/auth/login']);
      }),
      switchMap(() => throwError(() => refreshError))
    );
  }

  private clearCustomerSession(): void {
    this.authTokenStore.clear();
    this.authTokenStore.clearAuthCookies();
    localStorage.removeItem('userID');
  }

  private isAdminContext(): boolean {
    return typeof window !== 'undefined'
      && window.location.pathname.startsWith('/admin')
      && !!localStorage.getItem('adminID');
  }

  private impersonatingUserID(): string | null {
    if (localStorage.getItem('impersination') !== 'yes') {
      return null;
    }

    const userID = Number(localStorage.getItem('userID'));
    // mainUserID describes the current account's hierarchy and can be
    // overwritten by profile flows. The dedicated key preserves who started
    // this impersonation session.
    const impersonatingUserID = Number(
      localStorage.getItem('impersonatingUserID')
    );
    if (!Number.isInteger(impersonatingUserID) || impersonatingUserID <= 0 || impersonatingUserID === userID) {
      return null;
    }

    return String(impersonatingUserID);
  }

  private shouldHandleUnauthorized(error: HttpErrorResponse, requestUrl: string): boolean {
    if (error.status !== 401) {
      return false;
    }

    const failedUrl = `${error?.url || requestUrl || ''}`;
    const isApiRequest = failedUrl.includes(environment.apiUrl) || failedUrl.startsWith('/api/');
    const isAuthRequest = failedUrl.includes('/auth/');
    const refreshAllowedPaths = ['/auth/logout', '/auth/exit_impersonate_account', '/auth/impersonate_account'];
    const shouldRefreshAuthRequest = refreshAllowedPaths.some(path => failedUrl.includes(path));

    return isApiRequest && (!isAuthRequest || shouldRefreshAuthRequest);
  }
}
